import React, { useState } from 'react'
import { ChevronDownIcon, ChevronUpIcon } from '@heroicons/react/24/outline'
import SEO from '@components/common/SEO'

// Types
interface FAQItem {
  id: string
  question: string
  answer: string
  category: string
}

interface FAQCategoryData {
  title: string
  items: FAQItem[]
}

const FAQPage: React.FC = () => {
  const [openItems, setOpenItems] = useState<Set<string>>(new Set())

  const toggleItem = (id: string) => {
    const newOpenItems = new Set(openItems)
    if (newOpenItems.has(id)) {
      newOpenItems.delete(id)
    } else {
      newOpenItems.add(id)
    }
    setOpenItems(newOpenItems)
  }

  // FAQ Data organized by categories
  const faqCategories: FAQCategoryData[] = [
    {
      title: 'เกี่ยวกับเว็บไซต์',
      items: [
        {
          id: 'about-1',
          question: 'ไทยเซ็กคิวริตี้ไดเรกทอรี่คืออะไร?',
          answer: 'ไทยเซ็กคิวริตี้ไดเรกทอรี่ คือ แหล่งรวมข้อมูลบริษัทรักษาความปลอดภัยที่เชื่อถือได้ในประเทศไทย เราให้บริการค้นหาและเปรียบเทียบบริษัทรักษาความปลอดภัย พร้อมข้อมูลติดต่อครบถ้วน เพื่อช่วยให้คุณเลือกบริษัทที่เหมาะสมกับความต้องการได้อย่างง่ายดาย',
          category: 'about'
        },
        {
          id: 'about-2',
          question: 'เว็บไซต์นี้ให้บริการฟรีหรือไม่?',
          answer: 'ใช่ครับ การค้นหาและดูข้อมูลบริษัทเบื้องต้นสามารถใช้บริการได้ฟรี สำหรับสมาชิกที่ลงทะเบียนจะสามารถเข้าถึงข้อมูลติดต่อและคุณสมบัติขั้นสูงเพิ่มเติม',
          category: 'about'
        },
        {
          id: 'about-3',
          question: 'ข้อมูลบริษัทมีความน่าเชื่อถือแค่ไหน?',
          answer: 'เรามีทีมงานตรวจสอบและรวบรวมข้อมูลจากแหล่งที่เชื่อถือได้ รวมถึงการยืนยันข้อมูลกับบริษัทโดยตรง สำหรับบริษัทที่ผ่านการตรวจสอบจะมีเครื่องหมาย "ตรวจสอบแล้ว" แสดงไว้ให้ชัดเจน',
          category: 'about'
        }
      ]
    },
    {
      title: 'การค้นหาและใช้งาน',
      items: [
        {
          id: 'search-1',
          question: 'วิธีค้นหาบริษัทรักษาความปลอดภัยอย่างไร?',
          answer: 'คุณสามารถค้นหาได้หลายวิธี: 1) พิมพ์ชื่อบริษัทหรือคำค้นหาในช่องค้นหา 2) เลือกจังหวัดหรือพื้นที่ที่ต้องการ 3) กรองตามประเภทบริการ เช่น รักษาความปลอดภัยอาคาร โรงงาน งานอีเวนต์ หรือบอดี้การ์ด 4) เรียงลำดับตามความน่าเชื่อถือหรือปีที่ก่อตั้ง',
          category: 'search'
        },
        {
          id: 'search-2',
          question: 'สามารถค้นหาตามพื้นที่ได้หรือไม่?',
          answer: 'ได้ครับ เรามีระบบค้นหาตามจังหวัดและภูมิภาค คุณสามารถเลือกพื้นที่ที่ต้องการบริการได้ และระบบจะแสดงบริษัทที่ให้บริการในพื้นที่นั้นๆ พร้อมข้อมูลระยะทางและเขตพื้นที่บริการ',
          category: 'search'
        },
        {
          id: 'search-3',
          question: 'เห็นข้อมูลติดต่อไม่ครบต้องทำอย่างไร?',
          answer: 'สำหรับผู้ใช้งานทั่วไป (Guest) จะเห็นได้เฉพาะข้อมูลพื้นฐาน หากต้องการเห็นข้อมูลติดต่อครบถ้วน (เบอร์โทร อีเมล ไลน์) กรุณาลงทะเบียนเป็นสมาชิก (Member) ก่อนครับ',
          category: 'search'
        },
        {
          id: 'search-4',
          question: 'บริษัทที่มีป้าย "แนะนำ" หมายความว่าอย่างไร?',
          answer: 'บริษัทที่มีป้าย "แนะนำ" คือบริษัทที่ผ่านการตรวจสอบคุณสมบัติและมาตรฐานจากทีมงานเรา มีประวัติการให้บริการที่ดี ข้อมูลครบถ้วน และมีใบอนุญาตประกอบธุรกิจที่ถูกต้อง',
          category: 'search'
        }
      ]
    },
    {
      title: 'การสมัครสมาชิกและเข้าสู่ระบบ',
      items: [
        {
          id: 'member-1',
          question: 'ทำไมต้องสมัครสมาชิก?',
          answer: 'การสมัครสมาชิกจะทำให้คุณสามารถ: 1) เห็นข้อมูลติดต่อครบถ้วน (เบอร์โทร อีเมล ไลน์) 2) ใช้ฟีเจอร์ค้นหาขั้นสูง 3) บันทึกรายการบริษัทที่สนใจ 4) Claim บริษัทของคุณเองได้',
          category: 'member'
        },
        {
          id: 'member-2',
          question: 'วิธีสมัครสมาชิกอย่างไร?',
          answer: 'คลิกที่ปุ่ม "สมัครสมาชิก" มุมขวาบน กรอกข้อมูล: อีเมล, ชื่อ-นามสกุล, รหัสผ่าน ยอมรับข้อกำหนดการใช้งาน แล้วคลิก "สมัครสมาชิก" คุณจะสามารถเข้าใช้งานได้ทันที ไม่ต้องยืนยันอีเมล',
          category: 'member'
        },
        {
          id: 'member-3',
          question: 'ลืมรหัสผ่านต้องทำอย่างไร?',
          answer: 'คลิกที่ "ลืมรหัสผ่าน" ในหน้าเข้าสู่ระบบ กรอกอีเมลที่สมัครไว้ ระบบจะส่งลิงก์รีเซ็ตรหัสผ่านไปให้ทางอีเมล',
          category: 'member'
        },
        {
          id: 'member-4',
          question: 'สามารถเปลี่ยนข้อมูลส่วนตัวได้หรือไม่?',
          answer: 'ได้ครับ เข้าไปที่เมนู "โปรไฟล์" คุณสามารถแก้ไขชื่อ-นามสกุล เบอร์โทร และรหัสผ่านได้ (ไม่สามารถเปลี่ยนอีเมลได้)',
          category: 'member'
        }
      ]
    },
    {
      title: 'การ Claim บริษัท (สำหรับเจ้าของบริษัท)',
      items: [
        {
          id: 'claim-1',
          question: 'Claim บริษัทคืออะไร?',
          answer: 'การ Claim บริษัท คือการยืนยันตัวตนว่าคุณเป็นเจ้าของหรือผู้มีสิทธิ์จัดการบริษัทที่ลงทะเบียนไว้ในระบบ เมื่อ Claim สำเร็จ คุณจะสามารถแก้ไขข้อมูลบริษัท อัพเดทรายละเอียด และจัดการข้อมูลติดต่อได้',
          category: 'claim'
        },
        {
          id: 'claim-2',
          question: 'วิธี Claim บริษัทอย่างไร?',
          answer: '1) ลงทะเบียนเป็นสมาชิก 2) ค้นหาบริษัทของคุณ 3) คลิก "Claim บริษัทนี้" 4) กรอกข้อมูลและแนบเอกสารยืนยัน (ใบทะเบียนบริษัท, บัตรประชาชน, หนังสือมอบอำนาจ) 5) รอ Admin ตรวจสอบและอนุมัติ (ประมาณ 1-3 วันทำการ)',
          category: 'claim'
        },
        {
          id: 'claim-3',
          question: 'บริษัทของฉันยังไม่มีในระบบ จะเพิ่มได้อย่างไร?',
          answer: 'คุณสามารถส่งคำขอเพิ่มบริษัทใหม่ได้ โดย: 1) เข้าสู่ระบบในฐานะสมาชิก 2) ไปที่เมนู "My Claims" 3) คลิก "เพิ่มบริษัทใหม่" 4) กรอกข้อมูลและแนบเอกสาร 5) ส่งคำขอให้ Admin ตรวจสอบ',
          category: 'claim'
        },
        {
          id: 'claim-4',
          question: 'สามารถ Claim ได้หลายบริษัทไหม?',
          answer: 'ได้ครับ สมาชิกหนึ่งคนสามารถ Claim ได้หลายบริษัท (เช่น กรณีที่เป็นเจ้าของหลายบริษัท หรือเป็นผู้จัดการที่ได้รับมอบหมาย) แต่ละบริษัทต้องผ่านการอนุมัติจาก Admin แยกกัน',
          category: 'claim'
        },
        {
          id: 'claim-5',
          question: 'แก้ไขข้อมูลบริษัทที่ Claim แล้วได้ไหม?',
          answer: 'ได้ครับ แต่การแก้ไขทุกครั้งจะต้องผ่านระบบ Change Request คือ: 1) เข้าไปที่หน้าบริษัทของคุณ 2) คลิก "แก้ไขข้อมูล" 3) แก้ไขส่วนที่ต้องการ 4) ส่งคำขอให้ Admin อนุมัติ เพื่อรักษาคุณภาพข้อมูลในระบบ',
          category: 'claim'
        }
      ]
    },
    {
      title: 'ความปลอดภัยและความเป็นส่วนตัว',
      items: [
        {
          id: 'privacy-1',
          question: 'ข้อมูลของฉันปลอดภัยหรือไม่?',
          answer: 'เรามีระบบรักษาความปลอดภัยระดับสูง ข้อมูลส่วนตัวทั้งหมดถูกเข้ารหัสและเก็บอย่างปลอดภัย เราจะไม่เปิดเผยหรือขายข้อมูลของคุณให้บุคคลที่สาม สามารถอ่านรายละเอียดเพิ่มเติมได้ที่ "นโยบายความเป็นส่วนตัว"',
          category: 'privacy'
        },
        {
          id: 'privacy-2',
          question: 'เว็บไซต์เก็บข้อมูลอะไรบ้าง?',
          answer: 'เราเก็บข้อมูลเท่าที่จำเป็น เช่น: ชื่อ-นามสกุล, อีเมล, เบอร์โทร, ประวัติการค้นหา (เพื่อปรับปรุงบริการ), IP address และ Cookies (เพื่อความปลอดภัยและการทำงานของเว็บไซต์)',
          category: 'privacy'
        },
        {
          id: 'privacy-3',
          question: 'สามารถลบบัญชีได้ไหม?',
          answer: 'ได้ครับ คุณสามารถขอลบบัญชีได้โดยติดต่อมาที่ info@koder3.com เราจะลบข้อมูลส่วนตัวของคุณภายใน 7 วันทำการ (ข้อมูล Log บางส่วนอาจเก็บไว้ตามข้อกำหนดทางกฎหมาย)',
          category: 'privacy'
        }
      ]
    },
    {
      title: 'ติดต่อและขอความช่วยเหลือ',
      items: [
        {
          id: 'contact-1',
          question: 'พบปัญหาในการใช้งาน ติดต่อได้ที่ไหน?',
          answer: 'คุณสามารถติดต่อเราได้หลายช่องทาง: 1) อีเมล: info@koder3.com 2) แบบฟอร์มติดต่อในหน้า "ติดต่อเรา" 3) แจ้งปัญหาผ่านหน้า "รายงานปัญหา" เราจะตอบกลับภายใน 24-48 ชั่วโมง',
          category: 'contact'
        },
        {
          id: 'contact-2',
          question: 'พบข้อมูลบริษัทที่ไม่ถูกต้องต้องแจ้งอย่างไร?',
          answer: 'กรุณาแจ้งมาที่ "รายงานปัญหา" โดยระบุ: 1) ชื่อบริษัท 2) ข้อมูลที่ผิดพลาด 3) ข้อมูลที่ถูกต้อง (ถ้ามี) เราจะตรวจสอบและแก้ไขโดยเร็วที่สุด',
          category: 'contact'
        },
        {
          id: 'contact-3',
          question: 'ต้องการเพิ่มบริษัทเข้าระบบ ต้องทำอย่างไร?',
          answer: 'หากคุณเป็นเจ้าของบริษัท: ลงทะเบียนเป็นสมาชิก แล้วส่งคำขอเพิ่มบริษัทผ่านหน้า "My Claims" | หากเป็นบุคคลทั่วไป: ติดต่อมาที่ info@koder3.com พร้อมข้อมูลบริษัทที่ต้องการเพิ่ม',
          category: 'contact'
        }
      ]
    }
  ]

  // Generate structured data for SEO
  const faqStructuredData = {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    "mainEntity": faqCategories.flatMap(category =>
      category.items.map(item => ({
        "@type": "Question",
        "name": item.question,
        "acceptedAnswer": {
          "@type": "Answer",
          "text": item.answer
        }
      }))
    )
  }

  return (
    <>
      <SEO
        title="คำถามที่พบบ่อย (FAQ)"
        description="คำถามและคำตอบที่พบบ่อยเกี่ยวกับการใช้งานไทยเซ็กคิวริตี้ไดเรกทอรี่ - ค้นหาบริษัทรักษาความปลอดภัย การสมัครสมาชิก และการ Claim บริษัท"
        keywords="FAQ, คำถามที่พบบ่อย, วิธีใช้งาน, บริษัทรักษาความปลอดภัย, claim บริษัท"
      />

      {/* Structured Data for Rich Snippets */}
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(faqStructuredData) }}
      />

      <div className="bg-gradient-to-b from-primary-50 to-white min-h-screen">
        {/* Hero Section */}
        <div className="bg-primary-600 text-white py-16">
          <div className="container-wrapper">
            <div className="max-w-3xl mx-auto text-center">
              <h1 className="text-4xl md:text-5xl font-bold mb-4">
                คำถามที่พบบ่อย
              </h1>
              <p className="text-xl text-primary-100">
                คำตอบสำหรับคำถามที่ผู้ใช้งานมักถามบ่อย
              </p>
            </div>
          </div>
        </div>

        {/* FAQ Content */}
        <div className="container-wrapper py-12">
          <div className="max-w-4xl mx-auto">
            {/* Introduction */}
            <div className="bg-white rounded-lg shadow-sm border border-neutral-200 p-6 mb-8">
              <p className="text-neutral-700 leading-relaxed">
                ยินดีต้อนรับสู่ศูนย์ช่วยเหลือของไทยเซ็กคิวริตี้ไดเรกทอรี่
                ที่นี่รวบรวมคำถามและคำตอบที่ผู้ใช้งานมักถามบ่อยเกี่ยวกับการใช้งานเว็บไซต์
                การค้นหาบริษัท การสมัครสมาชิก และการ Claim บริษัท
                หากไม่พบคำตอบที่ต้องการ สามารถติดต่อทีมงานได้ตลอดเวลา
              </p>
            </div>

            {/* FAQ Categories */}
            <div className="space-y-8">
              {faqCategories.map((category, categoryIndex) => (
                <div key={categoryIndex} className="space-y-4">
                  {/* Category Title */}
                  <h2 className="text-2xl font-bold text-neutral-900 border-l-4 border-primary-600 pl-4">
                    {category.title}
                  </h2>

                  {/* FAQ Items */}
                  <div className="space-y-3">
                    {category.items.map((item) => {
                      const isOpen = openItems.has(item.id)

                      return (
                        <div
                          key={item.id}
                          className="bg-white rounded-lg shadow-sm border border-neutral-200 overflow-hidden transition-all duration-200 hover:shadow-md"
                        >
                          {/* Question Button */}
                          <button
                            onClick={() => toggleItem(item.id)}
                            className="w-full px-6 py-4 flex items-start justify-between text-left hover:bg-neutral-50 transition-colors duration-200"
                            aria-expanded={isOpen}
                          >
                            <span className="font-semibold text-neutral-900 pr-4 flex-1">
                              {item.question}
                            </span>
                            <span className="flex-shrink-0 text-primary-600">
                              {isOpen ? (
                                <ChevronUpIcon className="w-5 h-5" />
                              ) : (
                                <ChevronDownIcon className="w-5 h-5" />
                              )}
                            </span>
                          </button>

                          {/* Answer */}
                          {isOpen && (
                            <div className="px-6 pb-4 pt-2 border-t border-neutral-100">
                              <p className="text-neutral-700 leading-relaxed whitespace-pre-line">
                                {item.answer}
                              </p>
                            </div>
                          )}
                        </div>
                      )
                    })}
                  </div>
                </div>
              ))}
            </div>

            {/* Contact Section */}
            <div className="mt-12 bg-gradient-to-r from-primary-600 to-primary-700 rounded-lg shadow-lg p-8 text-white text-center">
              <h2 className="text-2xl font-bold mb-3">
                ยังมีคำถามเพิ่มเติม?
              </h2>
              <p className="text-primary-100 mb-6 text-lg">
                ติดต่อทีมงานของเราได้ตลอดเวลา เรายินดีให้ความช่วยเหลือ
              </p>
              <div className="flex flex-col sm:flex-row gap-4 justify-center items-center">
                <a
                  href="/contact"
                  className="inline-flex items-center px-6 py-3 bg-white text-primary-600 font-semibold rounded-lg hover:bg-primary-50 transition-colors duration-200 shadow-md"
                >
                  ติดต่อเรา
                </a>
                <a
                  href="/report"
                  className="inline-flex items-center px-6 py-3 bg-primary-500 text-white font-semibold rounded-lg hover:bg-primary-400 transition-colors duration-200"
                >
                  รายงานปัญหา
                </a>
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  )
}

export default FAQPage
